import { Box, MenuItem, Select, SelectProps, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { CsrStatus } from "../types";
import { csrStatusColors, csrStatusValues } from "../utils/tripTypeHelpers";

/**
 * Colored status indicator box used in renderValue and MenuItems
 */
const StatusBadge: React.FC<{ status: CsrStatus; label: string; showIcon?: boolean }> = ({
  status,
  label,
  showIcon = false,
}) => {
  const colors = csrStatusColors[status] || csrStatusColors.requested;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        pl: 1.5,
        pr: showIcon ? 0.5 : 1.5,
        py: 0.5,
        borderRadius: 1,
        bgcolor: colors.bg,
        border: `1.5px solid ${colors.border}`,
        color: colors.text,
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    >
      {label}
      {showIcon && (
        <KeyboardArrowDownIcon
          sx={{
            fontSize: "1rem",
            color: colors.text,
            opacity: 0.7,
          }}
        />
      )}
    </Box>
  );
};

/**
 * Content for menu item with colored dot
 */
const StatusMenuItemContent: React.FC<{ status: CsrStatus; label: string }> = ({
  status,
  label,
}) => {
  const colors = csrStatusColors[status];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "3px",
          bgcolor: colors.text,
        }}
      />
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: colors.text,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

interface CsrStatusSelectProps {
  value: CsrStatus;
  onChange: (newStatus: CsrStatus) => void;
  disabled?: boolean;
  /** Variant: "badge" shows styled badge (default), "standard" uses MUI standard Select */
  variant?: "badge" | "standard";
  /** Additional Select props */
  selectProps?: Partial<SelectProps<CsrStatus>>;
}

/**
 * Reusable CSR status dropdown with colored indicators.
 *
 * Usage:
 * ```tsx
 * <CsrStatusSelect
 *   value={trip.csr_status || "requested"}
 *   onChange={(newStatus) => handleStatusChange(newStatus)}
 *   disabled={trip.status === "completed"}
 * />
 * ```
 */
export const CsrStatusSelect: React.FC<CsrStatusSelectProps> = ({
  value,
  onChange,
  disabled = false,
  variant = "badge",
  selectProps = {},
}) => {
  const { t } = useTranslation();

  const getLabel = (status: CsrStatus): string => {
    return t(`screens.schedule.scheduleList.filters.csrStatusOptions.${status}`);
  };

  return (
    <Select<CsrStatus>
      value={value}
      size="small"
      variant={variant === "standard" ? "standard" : "outlined"}
      {...(variant === "standard" && { disableUnderline: true })}
      onChange={(e) => onChange(e.target.value as CsrStatus)}
      disabled={disabled}
      renderValue={(val) => <StatusBadge status={val} label={getLabel(val)} showIcon />}
      IconComponent={() => null}
      sx={{
        minWidth: 120,
        "& .MuiSelect-select": {
          padding: "4px 8px !important",
          display: "flex",
          alignItems: "center",
        },
        ...(variant === "badge" && {
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }),
        ...selectProps?.sx,
      }}
      MenuProps={{
        sx: { zIndex: 10000 },
        ...selectProps?.MenuProps,
      }}
      {...selectProps}
    >
      {csrStatusValues.map((status) => (
        <MenuItem key={status} value={status}>
          <StatusMenuItemContent status={status} label={getLabel(status)} />
        </MenuItem>
      ))}
    </Select>
  );
};
